"use strict";

// Admin view of QA reviewers' timesheets for a project: calculated and claimed
// hours, flags, and the actions that decide pay (approve once, mark not
// payable, or correct an approved timesheet with an adjustment). Hidden when
// the backend has time tracking off.
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { getLanguageName } from "../../utils/generalFunctions";
import QAActivityDialog from "./QAActivityDialog";
import {
    addQATimesheetAdjustment,
    approveQATimesheet,
    getQATimesheets,
    markQATimesheetNotPayable,
} from "./api";

export const FLAG_LABELS = {
    adjusted_over_15_percent: "Hours changed by over 15%",
    daily_cap_reached: "Daily cap reached",
    no_actions_over_60_minutes: "Over 60 min with no edits",
    unconfirmed_by_reviewer: "Not confirmed by reviewer",
};

const STATE_LABELS = {
    unconfirmed: "Not confirmed",
    confirmed: "Awaiting approval",
    approved: "Approved",
    not_payable: "Not payable",
};

const STATE_COLORS = {
    unconfirmed: "warning",
    confirmed: "info",
    approved: "success",
    not_payable: "default",
};

const ACTIONS = {
    approve: { title: "Approve timesheet", button: "Approve" },
    notPayable: { title: "Mark as not payable", button: "Mark not payable" },
    adjust: { title: "Add a correction", button: "Add correction" },
};

const isOpen = (timesheet) => ["confirmed", "unconfirmed"].includes(timesheet.state);

const QATimesheetsPanel = ({ projectId, names = {} }) => {
    const [data, setData] = useState(null);
    const [action, setAction] = useState(null); // { kind, timesheet }
    const [activity, setActivity] = useState(null);
    const [hours, setHours] = useState("");
    const [reason, setReason] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data: result } = await getQATimesheets(projectId);
            setData(result);
        } catch (loadError) {
            // 404: time tracking is off.
            if (loadError?.response?.status !== 404) {
                console.error("Unable to load QA timesheets", loadError);
            }
            setData(null);
        }
    }, [projectId]);

    useEffect(() => {
        if (projectId) load();
    }, [projectId, load]);

    const open = (kind, timesheet) => {
        setAction({ kind, timesheet });
        setHours(kind === "approve" ? String(timesheet.claimedHours) : "");
        setReason("");
        setError("");
    };

    const nameOf = (userId) => names[userId] || userId;

    const run = async () => {
        const { kind, timesheet } = action;
        const value = Number(hours);
        if (kind !== "notPayable" && (hours === "" || !Number.isFinite(value))) {
            setError("Enter the hours as a number");
            return;
        }
        if (kind === "adjust" && value === 0) {
            setError("A correction needs a non-zero number of hours");
            return;
        }
        if (kind !== "approve" && !reason.trim()) {
            setError("A reason is required");
            return;
        }
        setBusy(true);
        try {
            if (kind === "approve") {
                await approveQATimesheet(timesheet.id, value);
            } else if (kind === "notPayable") {
                await markQATimesheetNotPayable(timesheet.id, reason.trim());
            } else {
                await addQATimesheetAdjustment(timesheet.id, { hours: value, reason: reason.trim() });
            }
            toast.success(`${ACTIONS[kind].button}: done`);
            setAction(null);
            await load();
        } catch (runError) {
            setError(runError?.response?.data?.error || "Something went wrong");
        } finally {
            setBusy(false);
        }
    };

    if (!data || (!data.timesheets?.length && !data.missing?.length)) return null;

    return (
        <>
            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
                QA timesheets
            </Typography>
            <Table size="small" sx={{ width: "100%", bgcolor: "white" }}>
                <TableHead>
                    <TableRow>
                        <TableCell>QA Reviewer</TableCell>
                        <TableCell>Language</TableCell>
                        <TableCell>Cycle</TableCell>
                        <TableCell>Recorded</TableCell>
                        <TableCell>Claimed</TableCell>
                        <TableCell>Flags</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>Owed</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.timesheets.map((timesheet) => (
                        <TableRow key={timesheet.id}>
                            <TableCell>{nameOf(timesheet.userId)}</TableCell>
                            <TableCell>{getLanguageName(timesheet.language)}</TableCell>
                            <TableCell>
                                {timesheet.cycle}
                                {timesheet.isRework && (
                                    <Chip size="small" label="Rework" sx={{ ml: 1 }} />
                                )}
                            </TableCell>
                            <TableCell>{timesheet.calculatedHours} h</TableCell>
                            <TableCell>
                                {timesheet.reason ? (
                                    <Tooltip title={timesheet.reason}>
                                        <span>{timesheet.claimedHours} h *</span>
                                    </Tooltip>
                                ) : (
                                    `${timesheet.claimedHours} h`
                                )}
                            </TableCell>
                            <TableCell>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {timesheet.flags.map((flag) => (
                                        <Chip
                                            key={flag}
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                            label={FLAG_LABELS[flag] || flag}
                                        />
                                    ))}
                                </Stack>
                            </TableCell>
                            <TableCell>
                                <Tooltip title={timesheet.decisionReason || ""}>
                                    <Chip
                                        size="small"
                                        label={STATE_LABELS[timesheet.state] || timesheet.state}
                                        color={STATE_COLORS[timesheet.state]}
                                    />
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                {timesheet.amountOwed
                                    ? `${timesheet.amountOwed} ${timesheet.currency}`
                                    : "—"}
                                {timesheet.rateSource === "default" && (
                                    <Tooltip title="Paid at the default rate: this reviewer had no rate of their own">
                                        <Chip size="small" label="Default rate" sx={{ ml: 1 }} />
                                    </Tooltip>
                                )}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                <Button
                                    size="small"
                                    onClick={() =>
                                        setActivity({
                                            projectId,
                                            userId: timesheet.userId,
                                            name: nameOf(timesheet.userId),
                                            language: timesheet.language,
                                            from: timesheet.periodStart || undefined,
                                            to: timesheet.periodEnd,
                                        })
                                    }
                                >
                                    Activity
                                </Button>
                                {isOpen(timesheet) && (
                                    <>
                                        <Button size="small" onClick={() => open("approve", timesheet)}>
                                            Approve
                                        </Button>
                                        <Button size="small" onClick={() => open("notPayable", timesheet)}>
                                            Not payable
                                        </Button>
                                    </>
                                )}
                                {timesheet.state === "approved" && (
                                    <Button size="small" onClick={() => open("adjust", timesheet)}>
                                        Correct
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                    {data.missing.map((gap) => (
                        <TableRow key={`missing-${gap.userId}-${gap.language}-${gap.cycle}`}>
                            <TableCell>{gap.name || nameOf(gap.userId)}</TableCell>
                            <TableCell>{getLanguageName(gap.language)}</TableCell>
                            <TableCell>{gap.cycle}</TableCell>
                            <TableCell colSpan={6}>
                                <Chip size="small" color="error" label="Submitted, no timesheet" />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <QAActivityDialog target={activity} onClose={() => setActivity(null)} />

            <Dialog
                open={Boolean(action)}
                onClose={() => !busy && setAction(null)}
                fullWidth
                maxWidth="xs"
            >
                {action && (
                    <>
                        <DialogTitle>{ACTIONS[action.kind].title}</DialogTitle>
                        <DialogContent>
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                    {nameOf(action.timesheet.userId)},{" "}
                                    {getLanguageName(action.timesheet.language)}, cycle{" "}
                                    {action.timesheet.cycle}: recorded{" "}
                                    {action.timesheet.calculatedHours} h, claimed{" "}
                                    {action.timesheet.claimedHours} h.
                                </Typography>
                                {action.kind !== "notPayable" && (
                                    <TextField
                                        size="small"
                                        type="number"
                                        label={action.kind === "approve" ? "Hours to pay" : "Hours (negative to remove)"}
                                        value={hours}
                                        onChange={(event) => setHours(event.target.value)}
                                        inputProps={{ step: 0.25 }}
                                    />
                                )}
                                {action.kind !== "approve" && (
                                    <TextField
                                        size="small"
                                        label="Reason"
                                        value={reason}
                                        onChange={(event) => setReason(event.target.value)}
                                        multiline
                                        minRows={2}
                                    />
                                )}
                                {action.kind === "approve" && (
                                    <Typography variant="caption">
                                        Paid at the reviewer&apos;s rate for this period. This
                                        can&apos;t be undone; later changes are corrections.
                                    </Typography>
                                )}
                                {error && <Alert severity="error">{error}</Alert>}
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setAction(null)} disabled={busy}>
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={run} disabled={busy}>
                                {ACTIONS[action.kind].button}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </>
    );
};

export default QATimesheetsPanel;
